import { EMR_APIS } from "@/api_framework/api_config";

import { IEMRCreateResponse, IEMRCreateResponseData } from "@/api_framework/api_modals/EMRs";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { setLoading } from "@/app_redux/reducers/loading_reducer";

import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

import { setActiveCartId } from "@/app_redux/reducers/slices/auth/cart_slice";
import AxiosCurlirize from "axios-curlirize";
import { useAPIUtils } from "../../useAPIUtils";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";

AxiosCurlirize(axios, () => {});
export const useCreateEMRAPI = () => {
  const [newDrafEMRId, setNewDraftEMRId] = useState<string>(null);
  const [newCartId, setNewCartId] = useState<string>(null);
  const configuration = useConfiguration();
  const dispatch = useAppDispatch();
  const { captureError } = useAPIUtils();
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);

  const { sendCurl } = useCurlLogger();
  const execute = useCallback(
    async (userId: string, petId: string, clinicIdOverride?: string, suppressErrorToast = false) => {
      dispatch(
        setLoading({
          key: "isEMRCreateInProgress",
          status: true,
        }),
      );
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);

        const response: IEMRCreateResponseData = await axios
          .post(
            EMR_APIS?.CREATE_EMR?.baseURL ?? "",
            {
              customer_id: userId,
              pet_id: petId,
              clinic_id: clinicIdOverride || mappedClinicId,
            },
            {
              headers: {
                Authorization: `${accessToken}`,
                "Content-Type": "application/json",
              },
            },
          )
          .then((res: AxiosResponse<IEMRCreateResponse>) => {
            dispatch(
              setLoading({
                key: "isEMRCreateInProgress",
                status: false,
              }),
            );
            if (res.data.status === true && res.data) {
              setNewDraftEMRId(res.data.data.emr_id);
              setNewCartId(res.data.data.cart_id);
              dispatch(setActiveCartId(res.data.data.cart_id));
              return res.data.data;
            } else {
              setNewDraftEMRId(null);
              setNewCartId(null);
              dispatch(setActiveCartId(null));
              if (res.data?.data?.show_booking_status_modal) {
                return { emr_id: null, cart_id: null, show_booking_status_modal: true } as IEMRCreateResponseData;
              }
              return null;
            }
          })
          .catch((e: AxiosError) => {
            const responseData = e.response?.data as {
              message?: unknown;
              show_booking_status_modal?: boolean;
              data?: IEMRCreateResponseData;
            };
            const showBookingStatusModal = Boolean(
              responseData?.data?.show_booking_status_modal ?? responseData?.show_booking_status_modal,
            );
            if (!showBookingStatusModal) captureError(e);
            sendCurl(e.config?.["curlCommand"]);
            dispatch(
              setLoading({
                key: "isEMRCreateInProgress",
                status: false,
              }),
            );
            setNewDraftEMRId(null);
            setNewCartId(null);
            dispatch(setActiveCartId(null));

            if (showBookingStatusModal) {
              return { emr_id: null, cart_id: null, show_booking_status_modal: true } as IEMRCreateResponseData;
            }
            const responseStatus = e.response?.status ?? e.status;
            const responseMessage = JSON.stringify(responseData?.message ?? "");
            const activeEMRMatch = responseMessage.match(
              /Active EMR already exists for this pet:\s*([0-9a-f-]{36})/i,
            );
            if (suppressErrorToast && responseStatus === 400 && activeEMRMatch?.[1]) {
              return { emr_id: activeEMRMatch[1], cart_id: null } as IEMRCreateResponseData;
            }

            if (!suppressErrorToast && responseStatus === 400) {
              toast.error("Cannot Create EMR.");
            }
            if (!suppressErrorToast && responseStatus === 500) {
              toast.error("Cannot Create EMR.");
            }
            return null;
          });
        return response;
      } catch (e: any) {
        toast.error("Server Error: " + e.message);
      }
    },
    [dispatch],
  );
  return { newDrafEMRId, newCartId, execute };
};
